import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { Phase, RoadmapItem, RoadmapLevel } from '@shared/roadmap';

/**
 * Unified roadmap renderer used for BOTH the browser display and PDF export.
 *
 * Style decisions tuned for html2canvas v1.4:
 *  - Full borders (border: 1px solid X), never just border-left.
 *  - No fill backgrounds on the phase block; phase identity carried by the
 *    outer border color and a thin solid bar on top of each card.
 *  - All inline pills use `display: inline-flex; align-items: center; line-height: 1.2`
 *    so html2canvas can size them accurately.
 *  - No flex+gap layout for arranging children; uses `display: inline-block` +
 *    `vertical-align: middle` + `margin-right` instead.
 *  - 6-character hex everywhere — no 8-char alpha, no CSS variables.
 *  - `box-sizing: border-box` set explicitly on every container.
 *  - When `forceExpandAll` is true (PDF capture), the accordion grid wrapper
 *    is bypassed entirely and the body is rendered as a plain block.
 */

const PHASE_INFO: Record<
  Phase,
  { label: string; subtitle: string; color: string }
> = {
  immediate: { label: '即応', subtitle: '1ヶ月以内・優先度高', color: '#C42B2B' },
  short_term: { label: '短期', subtitle: '3ヶ月以内・優先度中', color: '#BA7517' },
  mid_term: { label: '中期', subtitle: '6ヶ月以内・優先度通常', color: '#0F6E56' },
};

const PHASE_ORDER: Phase[] = ['immediate', 'short_term', 'mid_term'];

const LEVEL_LABEL: Record<RoadmapLevel, string> = {
  1: '危機',
  2: '要改善',
  4: '良好',
  5: '最良',
};

const LEVEL_COLOR: Record<RoadmapLevel, string> = {
  1: '#C42B2B',
  2: '#BA7517',
  4: '#5DCAA5',
  5: '#0F6E56',
};

// ---------------- shared inline-style helpers ----------------

const sectionStyle: CSSProperties = {
  display: 'block',
  border: '1px solid #E2E8F0',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  padding: '24px',
  marginBottom: '24px',
  boxSizing: 'border-box',
};

const phaseBlockStyle = (color: string): CSSProperties => ({
  display: 'block',
  border: `1px solid ${color}`,
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  padding: '16px',
  marginBottom: '16px',
  boxSizing: 'border-box',
});

const phasePillStyle = (color: string): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: color,
  color: '#FFFFFF',
  padding: '4px 12px',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: 1.2,
  verticalAlign: 'middle',
  marginRight: '12px',
  boxSizing: 'border-box',
});

const levelPillStyle = (level: RoadmapLevel): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: LEVEL_COLOR[level],
  color: '#FFFFFF',
  padding: '2px 6px',
  borderRadius: '3px',
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1.2,
  verticalAlign: 'middle',
  marginRight: '12px',
  boxSizing: 'border-box',
});

const cardOuterStyle: CSSProperties = {
  display: 'block',
  border: '1px solid #E2E8F0',
  borderRadius: '6px',
  backgroundColor: '#FFFFFF',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

const cardTopBarStyle = (color: string): CSSProperties => ({
  display: 'block',
  height: '8px',
  backgroundColor: color,
});

const cardHeaderStyle = (clickable: boolean): CSSProperties => ({
  display: 'block',
  width: '100%',
  background: 'transparent',
  border: 'none',
  padding: '12px 16px',
  margin: 0,
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  cursor: clickable ? 'pointer' : 'default',
  boxSizing: 'border-box',
  lineHeight: 1.5,
});

const cardBodyStyle: CSSProperties = {
  display: 'block',
  borderTop: '1px solid #F1F5F9',
  padding: '12px 16px',
  boxSizing: 'border-box',
};

const actionTextStyle: CSSProperties = {
  display: 'block',
  margin: 0,
  fontSize: '13px',
  lineHeight: 1.7,
  color: '#334155',
  whiteSpace: 'pre-wrap',
};

// ---------------- subcomponents ----------------

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      width="12"
      height="12"
      style={{
        display: 'inline-block',
        verticalAlign: 'middle',
        marginRight: '6px',
        color: '#64748B',
        transform: open ? 'rotate(90deg)' : 'none',
        transition: 'transform 200ms ease-out',
      }}
      aria-hidden="true"
    >
      <path
        d="M4 2 L8 6 L4 10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LevelBadge({ level }: { level: RoadmapLevel }) {
  return (
    <span style={levelPillStyle(level)}>
      Lv.{level} {LEVEL_LABEL[level]}
    </span>
  );
}

function CardHeaderContent({
  item,
  showChevron,
  isOpen,
}: {
  item: RoadmapItem;
  showChevron: boolean;
  isOpen: boolean;
}) {
  return (
    <>
      {showChevron && <ChevronIcon open={isOpen} />}
      <span
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          color: '#94A3B8',
          fontSize: '11px',
          marginRight: '6px',
        }}
      >
        #{item.factor_id}
      </span>
      <span
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          color: '#1E293B',
          fontWeight: 600,
          fontSize: '14px',
          marginRight: '12px',
        }}
      >
        {item.factor_name}
      </span>
      <LevelBadge level={item.level} />
      <span
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          color: '#94A3B8',
          fontSize: '11px',
        }}
      >
        {item.layer_name} / {item.category_name}
      </span>
      {item.source !== 'default' && (
        <span
          style={{
            display: 'inline-block',
            verticalAlign: 'middle',
            color: '#378ADD',
            fontSize: '10px',
            fontWeight: 500,
            marginLeft: '8px',
          }}
        >
          ({item.source === 'diagnostic_override' ? '個別編集済み' : 'テンプレ編集済み'})
        </span>
      )}
    </>
  );
}

function RoadmapItemCard({
  item,
  phaseColor,
  isOpen,
  onToggle,
  onEdit,
  forceExpandAll,
  isLast,
}: {
  item: RoadmapItem;
  phaseColor: string;
  isOpen: boolean;
  onToggle: () => void;
  onEdit?: (item: RoadmapItem) => void;
  forceExpandAll: boolean;
  isLast: boolean;
}) {
  const interactive = !forceExpandAll;
  const showBody = forceExpandAll || isOpen;

  const body = (
    <div style={cardBodyStyle}>
      <p style={actionTextStyle}>{item.action}</p>
      {!forceExpandAll && onEdit && (
        <div style={{ marginTop: '12px', textAlign: 'right' }}>
          <button
            type="button"
            onClick={() => onEdit(item)}
            style={{
              display: 'inline-block',
              border: '1px solid #CBD5E1',
              backgroundColor: '#FFFFFF',
              color: '#334155',
              padding: '4px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            編集
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div
      style={{
        ...cardOuterStyle,
        marginBottom: isLast ? 0 : '8px',
      }}
    >
      <div style={cardTopBarStyle(phaseColor)} />
      {interactive ? (
        <button
          type="button"
          onClick={onToggle}
          style={cardHeaderStyle(true)}
          aria-expanded={isOpen}
        >
          <CardHeaderContent item={item} showChevron isOpen={isOpen} />
        </button>
      ) : (
        <div style={cardHeaderStyle(false)}>
          <CardHeaderContent item={item} showChevron={false} isOpen />
        </div>
      )}

      {/*
        In interactive mode we use a CSS-grid accordion for smooth animation.
        In export mode (forceExpandAll) we render the body directly because
        html2canvas v1.4 cannot reliably resolve grid-template-rows: 1fr.
      */}
      {forceExpandAll
        ? body
        : (
          <div
            style={{
              display: 'grid',
              gridTemplateRows: showBody ? '1fr' : '0fr',
              transition: 'grid-template-rows 200ms ease-out',
            }}
          >
            <div style={{ overflow: 'hidden' }}>{body}</div>
          </div>
        )}
    </div>
  );
}

// ---------------- main component ----------------

export function RoadmapSection({
  items,
  loading,
  onEdit,
}: {
  items: RoadmapItem[];
  loading?: boolean;
  /** When provided, the 編集 button becomes active and calls this with the item. */
  onEdit?: (item: RoadmapItem) => void;
}) {
  const forceExpandAll = false;
  const byPhase = useMemo(() => {
    const grouped: Record<Phase, RoadmapItem[]> = {
      immediate: [],
      short_term: [],
      mid_term: [],
    };
    for (const it of items) grouped[it.phase].push(it);
    return grouped;
  }, [items]);

  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  useEffect(() => {
    setExpanded(
      new Set(items.filter((i) => i.source !== 'default').map((i) => i.factor_id)),
    );
  }, [items]);

  const isOpen = (factorId: number) => expanded.has(factorId);

  const toggle = (factorId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(factorId)) next.delete(factorId);
      else next.add(factorId);
      return next;
    });
  };

  const expandPhase = (phase: Phase) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const it of byPhase[phase]) next.add(it.factor_id);
      return next;
    });
  };

  const collapsePhase = (phase: Phase) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const it of byPhase[phase]) next.delete(it.factor_id);
      return next;
    });
  };

  const phaseAllOpen = (phase: Phase) =>
    byPhase[phase].length > 0 && byPhase[phase].every((i) => expanded.has(i.factor_id));

  return (
    <section style={sectionStyle}>
      <div style={{ display: 'block', marginBottom: '16px' }}>
        <h2
          style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#1E293B',
            margin: 0,
            padding: 0,
          }}
        >
          改善ロードマップ
        </h2>
        <p
          style={{
            fontSize: '12px',
            color: '#64748B',
            margin: '4px 0 0 0',
            padding: 0,
          }}
        >
          診断結果に基づく実行計画。優先度順に「即応(1ヶ月以内)」「短期(3ヶ月以内)」「中期(6ヶ月以内)」の3フェーズで整理。
          {!forceExpandAll && ' 各カードをクリックすると詳細が展開されます。'}
        </p>
      </div>

      {loading && (
        <p style={{ fontSize: '13px', color: '#94A3B8', margin: 0 }}>
          ロードマップを読み込み中...
        </p>
      )}

      {!loading && items.length === 0 && (
        <p
          style={{
            border: '1px dashed #CBD5E1',
            borderRadius: '6px',
            backgroundColor: '#F8FAFC',
            padding: '24px',
            textAlign: 'center',
            fontSize: '13px',
            color: '#64748B',
            margin: 0,
          }}
        >
          診断結果に Lv.1〜2 / Lv.4〜5 の因子がないため、ロードマップは空です。
          <br />
          まずは診断入力画面で各因子のレベルを評価してください。
        </p>
      )}

      {!loading &&
        items.length > 0 &&
        PHASE_ORDER.map((phase, phaseIdx) => {
          const phaseItems = byPhase[phase];
          if (phaseItems.length === 0) return null;
          const info = PHASE_INFO[phase];
          const allOpen = phaseAllOpen(phase);
          const lastIdx = phaseItems.length - 1;
          const isLastPhaseShown =
            phaseIdx === PHASE_ORDER.length - 1 ||
            PHASE_ORDER.slice(phaseIdx + 1).every((p) => byPhase[p].length === 0);

          return (
            <div
              key={phase}
              style={{
                ...phaseBlockStyle(info.color),
                marginBottom: isLastPhaseShown ? 0 : '16px',
              }}
            >
              <div style={{ display: 'block', marginBottom: '12px', lineHeight: 1.5 }}>
                <span style={phasePillStyle(info.color)}>{info.label}</span>
                <span
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    color: info.color,
                    fontSize: '13px',
                    fontWeight: 600,
                    marginRight: '12px',
                  }}
                >
                  {info.subtitle}
                </span>
                <span
                  style={{
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    color: '#64748B',
                    fontSize: '11px',
                  }}
                >
                  {phaseItems.length}件
                </span>
                {!forceExpandAll && (
                  <button
                    type="button"
                    onClick={() =>
                      allOpen ? collapsePhase(phase) : expandPhase(phase)
                    }
                    style={{
                      float: 'right',
                      display: 'inline-block',
                      border: '1px solid #CBD5E1',
                      backgroundColor: '#FFFFFF',
                      color: '#334155',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    {allOpen ? '全て閉じる' : '全て開く'}
                  </button>
                )}
              </div>

              <div style={{ display: 'block' }}>
                {phaseItems.map((item, idx) => (
                  <RoadmapItemCard
                    key={item.factor_id}
                    item={item}
                    phaseColor={info.color}
                    isOpen={isOpen(item.factor_id)}
                    onToggle={() => toggle(item.factor_id)}
                    onEdit={onEdit}
                    forceExpandAll={forceExpandAll}
                    isLast={idx === lastIdx}
                  />
                ))}
              </div>
            </div>
          );
        })}
    </section>
  );
}
